import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import GroupScheduleCalendar from './components/GroupScheduleCalendar';
import AchievementList from '../home/components/AchievementList';
import AchievementForm from '../home/components/AchievementForm';
import MemberWeeklyTrendChart from './components/MemberWeeklyTrendChart';

interface RankingUser {
  user_id: string;
  username: string;
  total_hours: number;
  diary_hours: number;
  rank: number;
  avatar_url?: string;
}

interface StudyGroup {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  invite_code: string;
  member_count: number;
  is_member: boolean;
  created_at: string;
}

interface GroupSchedule {
  id: string;
  group_id: string;
  calendar_name: string;
  created_by: string;
  created_at: string;
}

interface GroupMember {
  user_id: string;
  username: string;
  avatar_url?: string;
  joined_at: string;
}

type PeriodType = 'month' | 'week' | 'today';

function useCountUp(target: number, duration = 1200) {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [target, duration]);

  return count;
}

// 流れ星パーティクル（強化版）
function StarParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* 通常の星 */}
      {[...Array(25)].map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute rounded-full bg-white"
          style={{
            width: `${Math.random() * 3 + 1}px`,
            height: `${Math.random() * 3 + 1}px`,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            opacity: Math.random() * 0.7 + 0.2,
            animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 4}s`,
          }}
        />
      ))}
      {/* 流れ星 */}
      {[...Array(5)].map((_, i) => (
        <div
          key={`shooting-${i}`}
          className="absolute"
          style={{
            top: `${Math.random() * 50}%`,
            left: `${Math.random() * 100}%`,
            animation: `shootingStar ${Math.random() * 3 + 4}s linear infinite`,
            animationDelay: `${Math.random() * 8}s`,
          }}
        >
          <div className="w-1 h-1 bg-white rounded-full shadow-[0_0_8px_2px_rgba(255,255,255,0.8)]"></div>
          <div className="absolute top-0 left-0 w-12 h-[1px] bg-gradient-to-r from-white to-transparent -rotate-45 origin-left"></div>
        </div>
      ))}
      {/* 浮遊する絵文字 */}
      {['⭐', '✨', '💫', '🌟'].map((emoji, i) => (
        <div
          key={`emoji-${i}`}
          className="absolute text-2xl opacity-20"
          style={{
            top: `${20 + i * 20}%`,
            left: `${10 + i * 20}%`,
            animation: `floatEmoji ${6 + i}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        >
          {emoji}
        </div>
      ))}
    </div>
  );
}

// 各バーのアニメーションを担当するコンポーネント
function BarItem({
  user, rank, barH, maxH, isMe, color, badge,
}: {
  user: RankingUser;
  rank: number;
  barH: number;
  maxH: number;
  isMe: boolean;
  color: string;
  badge: { bg: string; text: string; icon: string | null };
}) {
  const [animatedH, setAnimatedH] = useState(0);
  const animatedHours = useCountUp(user.total_hours, 1000 + rank * 80);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedH(barH);
    }, 120 + rank * 70);
    return () => clearTimeout(timer);
  }, [barH, rank]);

  return (
    <div key={user.user_id} className="flex flex-col items-center" style={{ width: '60px' }}>
      {/* 時間ラベル（カウントアップ） */}
      <span className={`text-[11px] font-bold tabular-nums mb-1 ${isMe ? 'text-teal-300' : 'text-white/60'}`}>
        {animatedHours}h
      </span>

      {/* ランクバッジ */}
      <div className="flex items-center justify-center mb-1">
        {rank <= 3 ? (
          <div className={`w-6 h-6 flex items-center justify-center rounded-full ${badge.bg} ${badge.text} text-[10px] font-black`}>
            {badge.icon || rank}
          </div>
        ) : (
          <div className={`w-6 h-6 flex items-center justify-center rounded-full ${badge.bg} ${badge.text} text-[10px] font-bold`}>
            {rank}
          </div>
        )}
      </div>

      {/* バー（下から伸びるアニメーション） */}
      <div
        className={`w-full rounded-t-lg bg-gradient-to-t ${color} relative ${isMe ? 'ring-2 ring-teal-400 ring-offset-1 ring-offset-transparent' : ''}`}
        style={{
          height: `${animatedH}px`,
          minHeight: animatedH > 0 ? '4px' : '0px',
          transition: 'height 0.85s cubic-bezier(0.34, 1.2, 0.64, 1)',
        }}
      >
        {rank === 1 && animatedH > 0 && (
          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-transparent rounded-t-lg animate-pulse"></div>
        )}
      </div>

      {/* ユーザー名 */}
      <div className={`mt-1.5 text-center px-0.5 ${isMe ? 'bg-teal-500/20 rounded-md py-0.5' : ''}`}>
        <p className={`text-[10px] font-medium leading-tight truncate max-w-[58px] ${isMe ? 'text-teal-200' : 'text-white/65'}`}>
          {user.username}
        </p>
        {isMe && <span className="text-[9px] text-teal-400 font-semibold">あなた</span>}
      </div>
    </div>
  );
}

// Top10バーチャート
function TopTenBarChart({ users, myUserId }: { users: RankingUser[]; myUserId?: string }) {
  const top10 = users.slice(0, 10);
  const maxHours = top10[0]?.total_hours || 1;
  const MAX_BAR_H = 180;

  const getBarColor = (rank: number) => {
    if (rank === 1) return 'from-yellow-400 to-amber-500';
    if (rank === 2) return 'from-slate-300 to-slate-400';
    if (rank === 3) return 'from-orange-300 to-orange-400';
    if (rank <= 6) return 'from-emerald-500 to-teal-500';
    return 'from-emerald-600/70 to-teal-600/70';
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return { bg: 'bg-yellow-400', text: 'text-black', icon: '👑' };
    if (rank === 2) return { bg: 'bg-slate-400', text: 'text-white', icon: null };
    if (rank === 3) return { bg: 'bg-orange-400', text: 'text-white', icon: null };
    return { bg: 'bg-white/15', text: 'text-white/80', icon: null };
  };

  return (
    <div className="overflow-x-auto pb-2">
      <div
        className="flex items-end justify-center gap-1.5 sm:gap-2.5 px-2 mx-auto"
        style={{ minHeight: `${MAX_BAR_H + 100}px`, minWidth: `${top10.length * 64}px` }}
      >
        {top10.map((user, index) => {
          const rank = index + 1;
          const barH = Math.max(Math.round((user.total_hours / maxHours) * MAX_BAR_H), 20);
          const isMe = user.user_id === myUserId;
          const badge = getRankBadge(rank);
          return (
            <BarItem
              key={user.user_id}
              user={user}
              rank={rank}
              barH={barH}
              maxH={MAX_BAR_H}
              isMe={isMe}
              color={getBarColor(rank)}
              badge={badge}
            />
          );
        })}
      </div>
      {/* Y軸目盛りライン */}
      <div className="mt-2 h-px bg-white/15 mx-2 rounded-full"></div>
    </div>
  );
}

// 表彰台カード（強化版）
function PodiumCard({ user, place, myUserId, avatarUrl }: { user: RankingUser; place: 1 | 2 | 3; myUserId?: string; avatarUrl?: string }) {
  const animatedHours = useCountUp(user.total_hours, 1000 + place * 200);
  const isMe = user.user_id === myUserId;

  const configs = {
    1: {
      crown: '👑',
      medal: '🥇',
      gradient: 'from-yellow-300 via-amber-400 to-yellow-500',
      glowColor: 'shadow-yellow-400/70',
      glowBg: 'bg-yellow-400/20',
      barH: 'h-32 sm:h-52',
      barGradient: 'from-yellow-400/80 to-amber-500/80',
      avatarSize: 'w-16 h-16 sm:w-24 sm:h-24',
      avatarGradient: 'from-yellow-300 to-amber-500',
      textSize: 'text-2xl sm:text-4xl',
      order: 'order-1 sm:order-2',
      rankBadge: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white',
      borderColor: 'border-yellow-400/50',
      scale: 'scale-105',
    },
    2: {
      crown: '',
      medal: '🥈',
      gradient: 'from-slate-300 via-slate-200 to-slate-400',
      glowColor: 'shadow-slate-400/50',
      glowBg: 'bg-slate-400/10',
      barH: 'h-24 sm:h-36',
      barGradient: 'from-slate-300/80 to-slate-400/80',
      avatarSize: 'w-14 h-14 sm:w-20 sm:h-20',
      avatarGradient: 'from-slate-300 to-slate-400',
      textSize: 'text-xl sm:text-3xl',
      order: 'order-2 sm:order-1',
      rankBadge: 'bg-gradient-to-r from-slate-300 to-slate-400 text-white',
      borderColor: 'border-slate-400/30',
      scale: '',
    },
    3: {
      crown: '',
      medal: '🥉',
      gradient: 'from-orange-300 via-amber-300 to-orange-400',
      glowColor: 'shadow-orange-400/50',
      glowBg: 'bg-orange-400/10',
      barH: 'h-20 sm:h-28',
      barGradient: 'from-orange-300/80 to-orange-400/80',
      avatarSize: 'w-14 h-14 sm:w-20 sm:h-20',
      avatarGradient: 'from-orange-300 to-orange-400',
      textSize: 'text-xl sm:text-3xl',
      order: 'order-3',
      rankBadge: 'bg-gradient-to-r from-orange-300 to-orange-400 text-white',
      borderColor: 'border-orange-400/30',
      scale: '',
    },
  };

  const cfg = configs[place];

  return (
    <div className={`flex flex-col items-center ${cfg.order} ${cfg.scale} transition-all duration-500 hover:scale-110`} style={{ minWidth: place === 1 ? 100 : 90 }}>
      {/* Crown for 1st with enhanced animation */}
      {place === 1 && (
        <div className="text-2xl sm:text-3xl mb-1 animate-bounce relative" style={{ animationDuration: '1.5s' }}>
          👑
          <div className="absolute inset-0 animate-ping opacity-30">👑</div>
        </div>
      )}

      {/* Avatar with enhanced glow */}
      <div className={`relative ${cfg.avatarSize} mx-auto mb-2`}>
        {isMe && (
          <>
            <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400 animate-pulse opacity-80 z-0 blur-md"></div>
            <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-teal-300 to-emerald-300 opacity-60 z-0 blur-sm animate-spin" style={{ animationDuration: '3s' }}></div>
          </>
        )}
        {place === 1 && (
          <>
            <div className="absolute -inset-3 rounded-full bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-300 opacity-40 z-0 blur-xl animate-pulse"></div>
            <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 opacity-60 z-0 blur-lg animate-spin" style={{ animationDuration: '4s' }}></div>
            <div className="absolute -inset-1 rounded-full bg-yellow-400/30 z-0 blur-md animate-ping"></div>
          </>
        )}
        {place === 2 && (
          <div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-slate-300 to-slate-400 opacity-50 z-0 blur-md animate-pulse"></div>
        )}
        {place === 3 && (
          <div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-orange-300 to-orange-400 opacity-50 z-0 blur-md animate-pulse"></div>
        )}
        <div className={`relative z-10 w-full h-full rounded-full bg-gradient-to-br ${cfg.avatarGradient} flex items-center justify-center shadow-2xl ${cfg.glowColor} border-2 ${cfg.borderColor} transition-transform duration-300 hover:rotate-12 overflow-hidden`}>
          {avatarUrl ? (
            <img src={avatarUrl} alt={user.username} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl sm:text-4xl">{cfg.medal}</span>
          )}
        </div>
      </div>

      {/* Name */}
      <div className={`text-center mb-1 px-1 max-w-[90px] sm:max-w-[110px] transition-all duration-300 ${isMe ? 'bg-teal-500/20 rounded-lg px-2 py-0.5 animate-pulse' : ''}`}>
        <p className={`font-bold text-[11px] sm:text-sm truncate ${isMe ? 'text-teal-200' : 'text-white'}`}>
          {user.username}
        </p>
        {isMe && <span className="text-[9px] sm:text-[10px] text-teal-300 font-semibold">あなた</span>}
      </div>

      {/* Hours with count-up animation */}
      <p className={`${cfg.textSize} font-black text-white drop-shadow-lg tabular-nums transition-all duration-300 hover:scale-110`}>
        {animatedHours}<span className="text-[10px] sm:text-sm font-bold opacity-70 ml-0.5">h</span>
      </p>

      {/* Bar with shimmer effect */}
      <div className={`mt-3 w-full ${cfg.barH} bg-gradient-to-t ${cfg.barGradient} rounded-t-xl backdrop-blur-sm border-t border-x ${cfg.borderColor} relative overflow-hidden group/bar`}>
        {place === 1 && (
          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-transparent animate-shimmer-vertical"></div>
        )}
      </div>
    </div>
  );
}

export default function RankingPage() {
  const { user, loading: authLoading } = useAuth();
  const [view, setView] = useState<'global' | 'group'>('global');
  const [period, setPeriod] = useState<PeriodType>('month');
  const [rankings, setRankings] = useState<RankingUser[]>([]);
  const [groups, setGroups] = useState<StudyGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<StudyGroup | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showJoinGroup, setShowJoinGroup] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [groupSchedules, setGroupSchedules] = useState<{ [groupId: string]: GroupSchedule }>({});
  const [showCreateSchedule, setShowCreateSchedule] = useState<string | null>(null);
  const [calendarName, setCalendarName] = useState('');
  const [openCalendarGroupId, setOpenCalendarGroupId] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [showMembersList, setShowMembersList] = useState(true);
  const [achievementRefreshKey, setAchievementRefreshKey] = useState(0);

  const myGroups = groups.filter(g => g.is_member);
  const myRank = rankings.find(r => r.user_id === user?.id);

  // ★ fetchRankings と fetchGroups を適切なタイミングで呼び出す
  useEffect(() => {
    if (!user) return;
    fetchRankings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, period, view, selectedGroup]);

  useEffect(() => {
    if (!user) return;
    fetchGroups();
    fetchGroupSchedules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchGroupMembers = async (groupId: string) => {
    setLoadingMembers(true);
    try {
      const { data: memberships, error: memberError } = await supabase
        .from('study_group_members')
        .select('user_id, joined_at')
        .eq('group_id', groupId)
        .order('joined_at', { ascending: true });

      if (memberError) throw memberError;

      const userIds = memberships?.map(m => m.user_id) || [];
      if (userIds.length === 0) {
        setGroupMembers([]);
        return;
      }

      const { data: profiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url')
        .in('user_id', userIds);

      if (profileError) throw profileError;

      const members: GroupMember[] = memberships.map(m => {
        const profile = profiles?.find(p => p.user_id === m.user_id);
        return {
          user_id: m.user_id,
          username: profile?.username || '不明なユーザー',
          avatar_url: profile?.avatar_url,
          joined_at: m.joined_at,
        };
      });

      setGroupMembers(members);
    } catch (error) {
      console.error('グループメンバーの取得に失敗しました:', error);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupMembers(selectedGroup.id);
    }
  }, [selectedGroup]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      let dateFilter: string | null = null;
      const now = new Date();
      if (period === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        dateFilter = today.toISOString();
      } else if (period === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        dateFilter = weekAgo.toISOString();
      } else if (period === 'month') {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        dateFilter = monthAgo.toISOString();
      }

      let query = supabase
        .from('diary_entries')
        .select('user_id, study_hours, created_at')
        .not('study_hours', 'is', null)
        .not('user_id', 'is', null); // user_idがnullの記録を除外
      if (dateFilter) query = query.gte('created_at', dateFilter);

      const { data: entries, error: diaryError } = await query;
      if (diaryError) throw diaryError;

      const userDiaryHours: { [key: string]: number } = {};
      const allUserIds = new Set<string>();

      entries?.forEach((entry) => {
        if (entry.user_id) {
          allUserIds.add(entry.user_id);
          userDiaryHours[entry.user_id] = (userDiaryHours[entry.user_id] || 0) + (Number(entry.study_hours) || 0);
        }
      });

      let userIds = Array.from(allUserIds);
      if (view === 'group' && selectedGroup) {
        const { data: members } = await supabase
          .from('study_group_members')
          .select('user_id')
          .eq('group_id', selectedGroup.id);
        const memberIds = members?.map(m => m.user_id) || [];
        userIds = userIds.filter(id => memberIds.includes(id));
      }

      if (userIds.length === 0) {
        setRankings([]);
        setLoading(false);
        return;
      }

      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url')
        .in('user_id', userIds);

      const rankingData: RankingUser[] = userIds
        .map((userId) => {
          const diaryH = userDiaryHours[userId] || 0;
          const profile = profiles?.find(p => p.user_id === userId);
          return {
            user_id: userId,
            username: profile?.username || 'Unknown',
            avatar_url: profile?.avatar_url,
            diary_hours: diaryH,
            total_hours: diaryH,
            rank: 0,
          } as RankingUser & { avatar_url?: string };
        })
        .filter(u => u.total_hours > 0)
        .sort((a, b) => b.total_hours - a.total_hours)
        .map((item, index) => ({ ...item, rank: index + 1 }));

      setRankings(rankingData);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    if (!user) return;
    try {
      const { data: allGroups, error } = await supabase.from('study_groups').select('*');
      if (error) throw error;
      const { data: memberships } = await supabase
        .from('study_group_members')
        .select('group_id')
        .eq('user_id', user.id);
      const memberGroupIds = memberships?.map(m => m.group_id) || [];
      const groupsWithMembers = await Promise.all(
        (allGroups || []).map(async (group) => {
          const { count } = await supabase
            .from('study_group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id);
          return { ...group, member_count: count || 0, is_member: memberGroupIds.includes(group.id) };
        })
      );
      setGroups(groupsWithMembers);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newGroupName.trim()) return;
    try {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const { data: newGroup, error } = await supabase
        .from('study_groups')
        .insert({ name: newGroupName.trim(), description: newGroupDescription.trim(), created_by: user.id, invite_code: code })
        .select()
        .single();
      if (error) throw error;
      await supabase.from('study_group_members').insert({ group_id: newGroup.id, user_id: user.id });
      setNewGroupName('');
      setNewGroupDescription('');
      setShowCreateGroup(false);
      fetchGroups();
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const joinGroup = async () => {
    if (!user || !inviteCode.trim()) return;
    try {
      const { data: group, error: groupError } = await supabase
        .from('study_groups')
        .select('id')
        .eq('invite_code', inviteCode.toUpperCase())
        .maybeSingle();
      if (groupError || !group) return;
      const { error } = await supabase.from('study_group_members').insert({ group_id: group.id, user_id: user.id });
      if (error) return;
      setInviteCode('');
      setShowJoinGroup(false);
      fetchGroups();
    } catch (error) {
      console.error('Error joining group:', error);
    }
  };

  const leaveGroup = async (groupId: string) => {
    if (!user) return;
    try {
      await supabase.from('study_group_members').delete().eq('group_id', groupId).eq('user_id', user.id);
      if (selectedGroup?.id === groupId) { setSelectedGroup(null); setView('global'); }
      fetchGroups();
    } catch (error) {
      console.error('Error leaving group:', error);
    }
  };

  const fetchGroupSchedules = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.from('group_schedules').select('*');
      if (error) throw error;
      const map: { [groupId: string]: GroupSchedule } = {};
      (data || []).forEach((schedule: GroupSchedule) => { map[schedule.group_id] = schedule; });
      setGroupSchedules(map);
    } catch (error) {
      console.error('Error fetching group schedules:', error);
    }
  };

  const createSchedule = async (groupId: string) => {
    if (!user || !calendarName.trim()) return;
    try {
      const { data, error } = await supabase
        .from('group_schedules')
        .insert({ group_id: groupId, calendar_name: calendarName.trim(), created_by: user.id })
        .select()
        .single();
      if (error) throw error;
      setGroupSchedules(prev => ({ ...prev, [groupId]: data }));
      setCalendarName('');
      setShowCreateSchedule(null);
      setOpenCalendarGroupId(groupId);
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
  };

  const getPeriodLabel = (p: PeriodType) => {
    switch (p) {
      case 'month': return '月間';
      case 'week': return '週間';
      case 'today': return '今日';
    }
  };

  const getPeriodIcon = (p: PeriodType) => {
    switch (p) {
      case 'month': return 'ri-calendar-line';
      case 'week': return 'ri-calendar-2-line';
      case 'today': return 'ri-sun-line';
    }
  };

  // ランキングリスト（4位以降）強化版
  const RankingListItem = ({ rankUser, index }: { rankUser: RankingUser & { avatar_url?: string }; index: number }) => {
    const animatedHours = useCountUp(rankUser.total_hours, 800 + index * 50);
    const isMe = rankUser.user_id === user?.id;
    const [isHovered, setIsHovered] = useState(false);
    
    return (
      <div
        className={`flex items-center gap-2 sm:gap-4 p-2.5 sm:p-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
          isMe
            ? 'bg-gradient-to-r from-teal-500/25 to-emerald-500/25 border-2 border-teal-400/60 shadow-lg shadow-teal-500/20 animate-pulse-slow'
            : 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 hover:shadow-xl hover:shadow-emerald-500/10'
        }`}
        style={{ 
          animationDelay: `${index * 60}ms`,
          animationFillMode: 'backwards'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Rank number with animation */}
        <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl font-black text-xs sm:text-sm shrink-0 transition-all duration-300 ${
          isMe
            ? 'bg-teal-500/30 text-teal-300'
            : 'bg-white/10 text-white/50'
        } ${isHovered ? 'scale-110 rotate-12' : ''}`}>
          {rankUser.rank}
        </div>

        {/* Avatar */}
        <div className="flex-shrink-0">
          {rankUser.avatar_url ? (
            <img
              src={rankUser.avatar_url}
              alt={rankUser.username}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-white/20"
            />
          ) : (
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white font-semibold border-2 border-white/20 text-xs sm:text-sm">
              {rankUser.username.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-xs sm:text-base truncate transition-colors duration-300 ${isMe ? 'text-teal-200' : isHovered ? 'text-emerald-300' : 'text-white/85'}`}>
            {rankUser.username}
          </p>
          {isMe && <span className="text-[10px] sm:text-[11px] text-teal-400 font-medium">あなた</span>}
        </div>

        {/* Progress bar with animation */}
        <div className="hidden sm:block w-20 lg:w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${isMe ? 'bg-gradient-to-r from-teal-400 to-emerald-400' : 'bg-gradient-to-r from-emerald-500/60 to-teal-500/60'}`}
            style={{ 
              width: `${Math.min((rankUser.total_hours / (rankings[0]?.total_hours || 1)) * 100, 100)}%`,
              transitionDelay: `${index * 50}ms`
            }}
          ></div>
        </div>

        {/* Hours with enhanced styling */}
        <div className="text-right shrink-0">
          <span className={`text-base sm:text-xl font-black tabular-nums transition-all duration-300 ${isMe ? 'text-teal-300' : isHovered ? 'text-emerald-300 scale-110' : 'text-emerald-400'}`}>
            {animatedHours}
          </span>
          <span className={`text-[10px] sm:text-xs font-bold ml-0.5 ${isMe ? 'text-teal-400/70' : 'text-white/40'}`}>h</span>
        </div>
      </div>
    );
  };

  // ランキングコンテンツ
  const RankingContent = () => (
    <>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 sm:py-20 gap-5">
          <div className="relative w-16 h-16 sm:w-20 sm:h-20">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500/20 animate-ping"></div>
            <div className="absolute inset-2 rounded-full border-4 border-emerald-400/30 animate-ping" style={{ animationDelay: '0.3s' }}></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-emerald-400 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center text-xl sm:text-2xl">🏆</div>
          </div>
          <p className="text-white/50 text-sm animate-pulse tracking-wide">ランキングを集計中...</p>
        </div>
      ) : rankings.length === 0 ? (
        <div className="text-center py-16 sm:py-20">
          <div className="text-6xl sm:text-7xl mb-5 animate-bounce" style={{ animationDuration: '2s' }}>🏆</div>
          <p className="text-white/60 text-base sm:text-lg font-semibold mb-2">まだランキングデータがありません</p>
          <p className="text-white/35 text-xs sm:text-sm mb-8">勉強日記に学習時間を記録するとランキングに反映されます</p>
        </div>
      ) : (
        <>
          {/* 自分の順位バナー */}
          {myRank && (
            <div className="mb-6 sm:mb-8 relative overflow-hidden rounded-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-600/30 via-emerald-600/30 to-teal-600/30"></div>
              <div className="absolute inset-0 border-2 border-teal-400/40 rounded-2xl"></div>
              <div className="relative flex items-center gap-3 sm:gap-4 p-3 sm:p-5">
                <div className="text-3xl sm:text-4xl">🎯</div>
                <div className="flex-1">
                  <p className="text-teal-300 text-[10px] sm:text-xs font-bold mb-0.5 tracking-wider uppercase">あなたの現在順位</p>
                  <p className="text-white font-black text-base sm:text-xl flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl text-yellow-300 drop-shadow-lg">{myRank.rank}</span>
                    <span className="text-white/40 text-xs sm:text-sm">位 / {rankings.length}人中</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white/40 text-[10px] sm:text-xs mb-0.5">総学習時間</p>
                  <p className="text-emerald-300 font-black text-xl sm:text-2xl tabular-nums">
                    {myRank.total_hours}<span className="text-xs sm:text-sm ml-0.5">h</span>
                  </p>
                  {myRank.rank > 1 && rankings[myRank.rank - 2] && (
                    <p className="text-white/30 text-[10px] sm:text-xs mt-0.5">
                      上位まで {rankings[myRank.rank - 2].total_hours - myRank.total_hours}h
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Top 10 バーチャート ── */}
          {rankings.length > 0 && (
            <div className="relative mb-8 sm:mb-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-white/10"></div>
                <p className="text-white/50 text-xs font-bold tracking-widest uppercase flex items-center gap-1.5">
                  <i className="ri-bar-chart-2-line text-emerald-400"></i>
                  TOP {Math.min(rankings.length, 10)}
                </p>
                <div className="h-px flex-1 bg-white/10"></div>
              </div>
              <TopTenBarChart users={rankings} myUserId={user?.id} />
            </div>
          )}

          {/* ── 全体リスト（11位〜） ── */}
          {rankings.length > 10 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="h-px flex-1 bg-white/10"></div>
                <p className="text-white/35 text-xs font-bold tracking-widest uppercase flex items-center gap-1.5">
                  <i className="ri-list-ordered text-sm"></i>
                  11位以降
                </p>
                <div className="h-px flex-1 bg-white/10"></div>
              </div>
              {rankings.slice(10).map((rankUser, i) => (
                <RankingListItem key={rankUser.user_id} rankUser={rankUser as RankingUser & { avatar_url?: string }} index={i} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.5); }
        }
        @keyframes shootingStar {
          0% { transform: translateX(0) translateY(0) rotate(-45deg); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translateX(300px) translateY(300px) rotate(-45deg); opacity: 0; }
        }
        @keyframes floatEmoji {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        @keyframes floatUp {
          0% { transform: translateY(0px); opacity: 1; }
          100% { transform: translateY(-60px); opacity: 0; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes shimmer-vertical {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .ranking-hero-gradient {
          background: linear-gradient(135deg, #064e3b 0%, #065f46 25%, #047857 50%, #059669 75%, #10b981 100%);
          background-size: 400% 400%;
          animation: gradientShift 12s ease infinite;
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .shimmer-text {
          background: linear-gradient(90deg, #fff 0%, #a7f3d0 30%, #fff 60%, #6ee7b7 80%, #fff 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        .period-btn-active {
          background: linear-gradient(135deg, #10b981, #059669);
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
          animation: glow-pulse 2s ease-in-out infinite;
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4); }
          50% { box-shadow: 0 6px 25px rgba(16, 185, 129, 0.6); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        @keyframes hero-fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-hero-fade-up {
          animation: hero-fade-up 0.8s ease-out;
        }
        .animate-hero-fade-up-delay {
          animation: hero-fade-up 0.8s ease-out 0.2s backwards;
        }
      `}</style>

      <Header />

      {/* 認証ローディング中 */}
      {authLoading ? (
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500/20 animate-ping"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-emerald-400 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center text-2xl">🏆</div>
          </div>
        </div>
      ) : !user ? (
        /* 未ログインユーザー向け画面 */
        <section className="pt-20 sm:pt-24 pb-16 hero-gradient-animated hero-dark-force relative overflow-hidden" style={{ minHeight: 'calc(100vh - 64px)', background: 'linear-gradient(135deg, #064e3b 0%, #065f46 25%, #047857 50%, #059669 75%, #10b981 100%)' }}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          </div>
          <StarParticles />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex flex-col items-center justify-center min-h-[calc(100vh-160px)] text-center">
            {/* アイコン */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-3xl sm:text-4xl" style={{ animation: 'floatEmoji 3s ease-in-out infinite' }}>🔥</span>
              <div className="relative">
                <span className="text-5xl sm:text-7xl drop-shadow-2xl inline-block" style={{ filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.8))' }}>🏆</span>
                <div className="absolute inset-0 animate-ping opacity-20">🏆</div>
              </div>
              <span className="text-3xl sm:text-4xl" style={{ animation: 'floatEmoji 3s ease-in-out infinite', animationDelay: '0.5s' }}>⚡</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-black text-white mb-3 tracking-tight">
              勉強時間<span className="shimmer-text">ランキング</span>
            </h1>
            <p className="text-teal-100 text-base sm:text-lg font-semibold mb-2">
              全国の学習者と勉強時間を競い合え！
            </p>
            <p className="text-white/60 text-sm mb-10">
              ランキング・グループ機能はログインユーザー限定です
            </p>

            {/* 機能紹介 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full mb-10">
              {[
                { icon: 'ri-bar-chart-line', label: 'グローバルランキング', desc: '全ユーザーの学習時間を比較' },
                { icon: 'ri-group-line', label: 'グループ機能', desc: '友達とグループを作って競い合う' },
                { icon: 'ri-calendar-line', label: 'グループスケジュール', desc: '共有カレンダーで予定を管理' },
              ].map((item) => (
                <div key={item.label} className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mx-auto mb-3">
                    <i className={`${item.icon} text-xl text-white`}></i>
                  </div>
                  <p className="text-white font-bold text-sm mb-1">{item.label}</p>
                  <p className="text-teal-100 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* ログインボタン */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/login"
                className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg flex items-center justify-center gap-2"
              >
                <i className="ri-login-box-line text-lg"></i>
                ログイン
              </Link>
              <Link
                to="/login"
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm flex items-center justify-center gap-2"
              >
                <i className="ri-user-add-line text-lg"></i>
                新規登録
              </Link>
            </div>
          </div>
        </section>
      ) : (
        /* ログイン済みユーザー向け通常表示 */
        <>
          {/* ヒーローヘッダー */}
          <section className="pt-20 sm:pt-24 pb-10 sm:pb-16 relative overflow-hidden ranking-hero-gradient">
            <StarParticles />

            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-emerald-400/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-32 -left-20 w-[600px] h-[600px] bg-teal-400/8 rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[400px] bg-green-400/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute top-20 left-20 w-[600px] h-[150px] bg-gradient-to-r from-transparent via-emerald-400/10 to-transparent -rotate-[15deg] blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950/60 to-transparent"></div>
              <div className="absolute top-0 left-1/4 w-[2px] h-full bg-gradient-to-b from-transparent via-emerald-400/20 to-transparent animate-pulse"></div>
              <div className="absolute top-0 right-1/3 w-[2px] h-full bg-gradient-to-b from-transparent via-teal-400/20 to-transparent animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
              <nav className="flex items-center gap-2 text-xs sm:text-sm text-emerald-300/70 mb-5 sm:mb-8 animate-hero-fade-up">
                <Link to="/" className="hover:text-white transition-colors">Top</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white font-medium">ランキング</span>
              </nav>

              <div className="text-center animate-hero-fade-up-delay">
                <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                  <span className="text-2xl sm:text-4xl transition-transform duration-300 hover:scale-125" style={{ animation: 'floatUp 3s ease-in-out infinite', animationDelay: '0s' }}>🔥</span>
                  <div className="relative">
                    <span className="text-4xl sm:text-6xl drop-shadow-2xl transition-transform duration-300 hover:scale-110 hover:rotate-12 inline-block" style={{ filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.8))' }}>🏆</span>
                    <div className="absolute inset-0 animate-ping opacity-30">🏆</div>
                  </div>
                  <span className="text-2xl sm:text-4xl transition-transform duration-300 hover:scale-125" style={{ animation: 'floatUp 3s ease-in-out infinite', animationDelay: '0.5s' }}>⚡</span>
                </div>

                <h1 className="text-2xl sm:text-5xl font-black text-white mb-2 sm:mb-3 tracking-tight">
                  勉強時間<span className="shimmer-text">ランキング</span>
                </h1>
                <p className="text-emerald-200/80 text-sm sm:text-lg font-semibold mb-1 sm:mb-2">
                  全国の学習者と勉強時間を競い合え！
                </p>
                <p className="text-white/40 text-xs sm:text-sm hidden sm:block">
                  勉強日記の総学習時間がリアルタイムで反映されます
                </p>

                {rankings.length > 0 && (
                  <div className="flex items-center justify-center gap-2 mt-5 sm:mt-7 flex-wrap px-2">
                    <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 hover:bg-white/15 transition-all duration-300">
                      <i className="ri-user-line text-emerald-300"></i>
                      {rankings.length}人が参加中
                    </div>
                    {rankings[0] && (
                      <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-yellow-400/20 backdrop-blur-sm rounded-full border border-yellow-400/30 text-yellow-200 text-xs sm:text-sm font-semibold flex items-center gap-1.5 animate-pulse-slow">
                        <span>👑</span>
                        <span className="hidden sm:inline">トップ: {rankings[0].username}（{rankings[0].total_hours}h）</span>
                        <span className="sm:hidden">{rankings[0].username} {rankings[0].total_hours}h</span>
                      </div>
                    )}
                    {myRank && (
                      <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 text-teal-200 text-xs sm:text-sm font-semibold flex items-center gap-1.5">
                        <span>🎯</span>
                        あなた: {myRank.rank}位
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">

            {/* View Toggle */}
            <FadeInSection>
              <div className="flex gap-2 sm:gap-4 mb-6 sm:mb-8">
                <button
                  onClick={() => { setView('global'); setSelectedGroup(null); }}
                  className={`flex-1 min-h-[48px] sm:min-h-[52px] px-3 sm:px-6 py-2.5 sm:py-3 rounded-2xl font-bold transition-all duration-300 text-xs sm:text-base whitespace-nowrap flex items-center justify-center gap-1.5 sm:gap-2 transform hover:scale-[1.02] ${
                    view === 'global'
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-xl shadow-emerald-500/30 scale-[1.02]'
                      : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <i className="ri-global-line text-base sm:text-lg"></i>
                  グローバル
                </button>
                <button
                  onClick={() => setView('group')}
                  className={`flex-1 min-h-[48px] sm:min-h-[52px] px-3 sm:px-6 py-2.5 sm:py-3 rounded-2xl font-bold transition-all duration-300 text-xs sm:text-base whitespace-nowrap flex items-center justify-center gap-1.5 sm:gap-2 transform hover:scale-[1.02] ${
                    view === 'group'
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-xl shadow-emerald-500/30 scale-[1.02]'
                      : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <i className="ri-group-line text-base sm:text-lg"></i>
                  グループ
                </button>
              </div>
            </FadeInSection>

            {/* Global Ranking */}
            {view === 'global' && (
              <FadeInSection>
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-4 sm:p-8 shadow-2xl relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5 pointer-events-none"></div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-400/5 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
                  
                  <div className="relative z-10">
                    <div className="flex gap-1.5 sm:gap-2 mb-6 sm:mb-8">
                      {(['month', 'week', 'today'] as PeriodType[]).map((p) => (
                        <button
                          key={p}
                          onClick={() => setPeriod(p)}
                          className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 sm:py-2.5 rounded-xl font-bold transition-all duration-300 text-xs sm:text-sm whitespace-nowrap flex items-center justify-center gap-1 sm:gap-1.5 ${
                            period === p
                              ? 'period-btn-active text-white'
                              : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/80'
                          }`}
                        >
                          <i className={`${getPeriodIcon(p)} text-xs sm:text-sm`}></i>
                          {getPeriodLabel(p)}
                        </button>
                      ))}
                    </div>

                    <RankingContent />
                  </div>
                </div>
              </FadeInSection>
            )}

            {/* Group Ranking - グループ一覧 */}
            {view === 'group' && !selectedGroup && (
              <FadeInSection>
                <div className="space-y-5 sm:space-y-6">
                  {myGroups.length === 0 && (
                    <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-emerald-500/10 rounded-3xl border-2 border-emerald-500/20 p-5 sm:p-10 mb-6 sm:mb-8">
                      <div className="text-center mb-6 sm:mb-8">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-emerald-500/20 rounded-full mx-auto mb-4">
                          <i className="ri-group-line text-3xl sm:text-4xl text-emerald-400"></i>
                        </div>
                        <h2 className="text-xl sm:text-3xl font-bold text-white mb-3">グループで勉強時間を競い合おう！</h2>
                        <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto">
                          友達とグループを作成して、お互いの学習時間をランキング形式で確認できます。
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-500/20 rounded-xl">
                              <i className="ri-add-circle-line text-xl text-emerald-400"></i>
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white">グループを作成する</h3>
                          </div>
                          <ol className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                            <li className="flex items-start gap-2 sm:gap-3 text-sm text-white/70">
                              <span className="flex items-center justify-center w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full font-bold text-xs shrink-0 mt-0.5">1</span>
                              <span>「作成」ボタンをクリック</span>
                            </li>
                            <li className="flex items-start gap-2 sm:gap-3 text-sm text-white/70">
                              <span className="flex items-center justify-center w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full font-bold text-xs shrink-0 mt-0.5">2</span>
                              <span>グループ名と説明を入力</span>
                            </li>
                            <li className="flex items-start gap-2 sm:gap-3 text-sm text-white/70">
                              <span className="flex items-center justify-center w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full font-bold text-xs shrink-0 mt-0.5">3</span>
                              <span>招待コードを友達に共有</span>
                            </li>
                          </ol>
                          <button
                            onClick={() => setShowCreateGroup(true)}
                            className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold flex items-center justify-center gap-2 text-sm"
                          >
                            <i className="ri-add-line text-lg"></i>
                            グループを作成
                          </button>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-teal-500/20 rounded-xl">
                              <i className="ri-login-box-line text-xl text-teal-400"></i>
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white">招待コードで参加する</h3>
                          </div>
                          <ol className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                            <li className="flex items-start gap-2 sm:gap-3 text-sm text-white/70">
                              <span className="flex items-center justify-center w-6 h-6 bg-teal-500/20 text-teal-400 rounded-full font-bold text-xs shrink-0 mt-0.5">1</span>
                              <span>友達から招待コードを受け取る</span>
                            </li>
                            <li className="flex items-start gap-2 sm:gap-3 text-sm text-white/70">
                              <span className="flex items-center justify-center w-6 h-6 bg-teal-500/20 text-teal-400 rounded-full font-bold text-xs shrink-0 mt-0.5">2</span>
                              <span>「参加」ボタンをクリック</span>
                            </li>
                            <li className="flex items-start gap-2 sm:gap-3 text-sm text-white/70">
                              <span className="flex items-center justify-center w-6 h-6 bg-teal-500/20 text-teal-400 rounded-full font-bold text-xs shrink-0 mt-0.5">3</span>
                              <span>招待コードを入力して参加</span>
                            </li>
                          </ol>
                          <button
                            onClick={() => setShowJoinGroup(true)}
                            className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-white/10 text-white border border-white/20 rounded-xl hover:bg-white/15 transition-all font-bold flex items-center justify-center gap-2 text-sm"
                          >
                            <i className="ri-login-box-line text-lg"></i>
                            招待コードで参加
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h2 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2">
                        <i className="ri-group-line text-emerald-400"></i>
                        参加中のグループ
                      </h2>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowJoinGroup(true)}
                          className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-white/5 text-emerald-300 border border-emerald-500/30 rounded-xl hover:bg-emerald-500/10 transition-all font-semibold whitespace-nowrap text-xs sm:text-sm flex items-center gap-1"
                        >
                          <i className="ri-login-box-line"></i>参加
                        </button>
                        <button
                          onClick={() => setShowCreateGroup(true)}
                          className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 font-semibold whitespace-nowrap text-xs sm:text-sm flex items-center gap-1"
                        >
                          <i className="ri-add-line"></i>作成
                        </button>
                      </div>
                    </div>

                    {myGroups.length === 0 ? (
                      <div className="bg-white/5 rounded-3xl border border-white/10 p-8 sm:p-16 text-center">
                        <div className="text-5xl sm:text-7xl mb-5 animate-bounce" style={{ animationDuration: '2s' }}>👥</div>
                        <p className="text-base sm:text-xl text-white/50 mb-2 font-semibold">まだグループに参加していません</p>
                        <p className="text-white/30 text-sm mb-8">上記の手順に従ってグループを作成または参加しましょう！</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                        {myGroups.map((group) => (
                          <div key={group.id} className="bg-white/5 rounded-2xl border border-white/10 p-4 sm:p-6 hover:bg-white/8 hover:border-emerald-500/30 transition-all duration-300 group/card">
                            <div className="flex items-start justify-between mb-3 sm:mb-4">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base sm:text-lg font-bold text-white mb-1 truncate group-hover/card:text-emerald-300 transition-colors">{group.name}</h3>
                                {group.description && (
                                  <p className="text-xs sm:text-sm text-white/45 line-clamp-2">{group.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-white/35 mb-4 sm:mb-5 flex-wrap">
                              <span className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full">
                                <i className="ri-user-line text-emerald-400"></i>{group.member_count}人
                              </span>
                              <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full font-mono">
                                <i className="ri-key-line text-emerald-400"></i>
                                <span className="text-[11px]">{group.invite_code}</span>
                                <button
                                  onClick={() => copyInviteCode(group.invite_code)}
                                  className="ml-1 p-0.5 hover:bg-white/10 rounded transition-colors"
                                  title="招待コードをコピー"
                                >
                                  <i className={`${copiedCode === group.invite_code ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line text-white/50'} text-xs`}></i>
                                </button>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setSelectedGroup(group)}
                                className="flex-1 min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold text-xs sm:text-sm shadow-md shadow-emerald-500/20"
                              >
                                詳細を見る
                              </button>
                              <button
                                onClick={() => leaveGroup(group.id)}
                                className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all"
                                title="退出"
                              >
                                <i className="ri-logout-box-line text-base sm:text-lg"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </FadeInSection>
            )}

            {/* Selected Group Details */}
            {view === 'group' && selectedGroup && (
              <FadeInSection>
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-4 sm:p-8 shadow-2xl">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 sm:gap-3 mb-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-500/20 rounded-xl">
                          <i className="ri-group-line text-emerald-400 text-base sm:text-xl"></i>
                        </div>
                        <h2 className="text-xl sm:text-3xl font-bold text-white break-words">{selectedGroup.name}</h2>
                      </div>
                      {selectedGroup.description && (
                        <p className="text-xs sm:text-base text-white/45 break-words ml-10 sm:ml-13">{selectedGroup.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedGroup(null)}
                      className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap border border-white/10 self-start sm:self-auto"
                    >
                      <i className="ri-arrow-left-line"></i>戻る
                    </button>
                  </div>

                  {/* グループメンバー一覧セクション */}
                  <div className="mb-6 sm:mb-8 bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                    <button
                      onClick={() => setShowMembersList(prev => !prev)}
                      className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                        <i className="ri-team-line text-emerald-400"></i>
                        グループメンバー
                        <span className="text-xs sm:text-sm text-white/50 bg-white/10 px-2.5 py-0.5 rounded-full font-normal">
                          {groupMembers.length}人
                        </span>
                      </h3>
                      {showMembersList ? <i className="ri-arrow-up-s-line text-white/50 text-xl transition-transform duration-300"></i> : <i className="ri-arrow-down-s-line text-white/50 text-xl transition-transform duration-300"></i>}
                    </button>

                    {showMembersList && (
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                        {loadingMembers ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
                          </div>
                        ) : groupMembers.length === 0 ? (
                          <div className="text-center py-8">
                            <i className="ri-user-line text-4xl text-white/20 mb-2"></i>
                            <p className="text-white/40 text-sm">メンバーがいません</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {groupMembers.map((member) => {
                              const isMe = member.user_id === user?.id;
                              return (
                                <div
                                  key={member.user_id}
                                  className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                                    isMe
                                      ? 'bg-gradient-to-r from-teal-500/20 to-emerald-500/20 border-2 border-teal-400/40'
                                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                                  }`}
                                >
                                  {/* アバター */}
                                  <div className="relative shrink-0">
                                    {member.avatar_url ? (
                                      <img
                                        src={member.avatar_url}
                                        alt={member.username}
                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white/20"
                                      />
                                    ) : (
                                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white font-bold text-base sm:text-lg border-2 border-white/20">
                                        {member.username.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    {isMe && (
                                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center border-2 border-gray-900">
                                        <i className="ri-user-star-fill text-white text-xs"></i>
                                      </div>
                                    )}
                                  </div>

                                  {/* ユーザー情報 */}
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-semibold text-sm truncate ${isMe ? 'text-teal-200' : 'text-white'}`}>
                                      {member.username}
                                    </p>
                                    {isMe && (
                                      <span className="text-xs text-teal-400 font-medium">あなた</span>
                                    )}
                                    {!isMe && (
                                      <p className="text-xs text-white/40">
                                        {new Date(member.joined_at).toLocaleDateString('ja-JP', { 
                                          year: 'numeric', 
                                          month: 'short', 
                                          day: 'numeric' 
                                        })}参加
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ===== メンバー週間学習時間推移グラフ ===== */}
                  {groupMembers.length > 0 && (
                    <MemberWeeklyTrendChart
                      groupId={selectedGroup.id}
                      groupName={selectedGroup.name}
                      memberUserIds={groupMembers.map(m => m.user_id)}
                    />
                  )}

                  <div className="flex gap-1.5 sm:gap-2 mb-6 sm:mb-8 mt-6">
                    {(['month', 'week', 'today'] as PeriodType[]).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 sm:py-2.5 rounded-xl font-bold transition-all duration-300 text-xs sm:text-sm whitespace-nowrap flex items-center justify-center gap-1 sm:gap-1.5 ${
                          period === p
                            ? 'period-btn-active text-white'
                            : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/80'
                        }`}
                      >
                        <i className={`${getPeriodIcon(p)} text-xs sm:text-sm`}></i>
                        {getPeriodLabel(p)}
                      </button>
                    ))}
                  </div>

                  <RankingContent />

                  {/* ===== グループスケジュールセクション ===== */}
                  <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/10">
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h3 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                        <i className="ri-calendar-line text-emerald-400 text-lg sm:text-xl"></i>グループスケジュール
                      </h3>
                      {groupSchedules[selectedGroup.id] ? (
                        <button
                          onClick={() => setOpenCalendarGroupId(selectedGroup.id)}
                          className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold text-xs sm:text-sm whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-calendar-check-line"></i>カレンダーを開く
                        </button>
                      ) : (
                        <button
                          onClick={() => { setCalendarName(''); setShowCreateSchedule(selectedGroup.id); }}
                          className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold text-xs sm:text-sm whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-add-line"></i>作成
                        </button>
                      )}
                    </div>

                    {groupSchedules[selectedGroup.id] ? (
                      <div
                        onClick={() => setOpenCalendarGroupId(selectedGroup.id)}
                        className="flex items-center gap-4 p-4 sm:p-5 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-emerald-500/30 transition-all cursor-pointer group/sched"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-500/20 rounded-xl shrink-0 group-hover/sched:bg-emerald-500/30 transition-colors">
                          <i className="ri-calendar-check-line text-emerald-400 text-xl sm:text-2xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-bold text-white truncate group-hover/sched:text-emerald-300 transition-colors">
                            {groupSchedules[selectedGroup.id].calendar_name}
                          </p>
                          <p className="text-xs text-white/40 mt-0.5">クリックしてカレンダーを開く</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-white/30 text-xl group-hover/sched:text-emerald-400 transition-colors shrink-0"></i>
                      </div>
                    ) : (
                      <div className="p-5 sm:p-8 bg-white/5 border border-dashed border-white/20 rounded-2xl text-center">
                        <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mx-auto mb-3">
                          <i className="ri-calendar-2-line text-2xl text-white/40"></i>
                        </div>
                        <p className="text-white/50 text-sm mb-1 font-medium">スケジュールがまだありません</p>
                        <p className="text-white/30 text-xs mb-4">グループ共有カレンダーを作成して、メンバーと予定を共有しましょう</p>
                        <button
                          onClick={() => { setCalendarName(''); setShowCreateSchedule(selectedGroup.id); }}
                          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold text-sm shadow-lg shadow-emerald-500/20 flex items-center gap-2 mx-auto"
                        >
                          <i className="ri-add-line"></i>カレンダーを作成
                        </button>
                      </div>
                    )}
                  </div>

                  {/* グループの達成報告セクション */}
                  <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/10">
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h3 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                        <i className="ri-trophy-line text-emerald-400 text-lg sm:text-xl"></i>グループの達成報告
                      </h3>
                      <button onClick={() => setShowAchievementForm(true)} className="min-h-[40px] sm:min-h-[44px] px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg font-bold text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 cursor-pointer">
                        <i className="ri-add-line"></i>投稿
                      </button>
                    </div>
                    <AchievementList groupId={selectedGroup.id} refreshKey={achievementRefreshKey} />
                  </div>
                </div>
              </FadeInSection>
            )}
          </div>

          <Footer />

          {/* Create Group Modal */}
          {showCreateGroup && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
              <div className="w-full sm:max-w-lg bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
                <div className="sm:hidden flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 bg-white/20 rounded-full"></div>
                </div>
                <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center rounded-t-3xl">
                  <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                    <i className="ri-group-line text-emerald-400"></i>新しいグループ
                  </h2>
                  <button onClick={() => setShowCreateGroup(false)} className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-colors">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <form onSubmit={handleCreateGroup} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">グループ名 <span className="text-red-400">*</span></label>
                    <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-white/25" placeholder="例: 情報工学科 2024" required />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">説明</label>
                    <textarea value={newGroupDescription} onChange={(e) => setNewGroupDescription(e.target.value)} className="w-full min-h-[90px] sm:min-h-[100px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none placeholder-white/25" placeholder="グループの説明を入力..." rows={3} />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                    <button type="button" onClick={() => setShowCreateGroup(false)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 text-white/50 bg-white/5 rounded-xl hover:bg-white/10 transition-colors font-semibold text-sm">キャンセル</button>
                    <button type="submit" className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-emerald-500/20 text-sm">作成する</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Join Group Modal */}
          {showJoinGroup && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="w-full sm:max-w-md bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl">
                <div className="sm:hidden flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 bg-white/20 rounded-full"></div>
                </div>
                <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center rounded-t-3xl">
                  <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                    <i className="ri-login-box-line text-emerald-400"></i>グループに参加
                  </h2>
                  <button onClick={() => setShowJoinGroup(false)} className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-colors">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">招待コード</label>
                    <input type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent uppercase font-mono tracking-widest" placeholder="例: ABC12345" />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                    <button onClick={() => setShowJoinGroup(false)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 text-white/50 bg-white/5 rounded-xl hover:bg-white/10 transition-colors font-semibold text-sm">キャンセル</button>
                    <button onClick={joinGroup} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-emerald-500/20 text-sm">参加する</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Schedule Modal */}
          {showCreateSchedule && (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="w-full sm:max-w-md bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl">
                <div className="sm:hidden flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 bg-white/20 rounded-full"></div>
                </div>
                <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center rounded-t-3xl">
                  <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                    <i className="ri-calendar-line text-emerald-400"></i>カレンダー作成
                  </h2>
                  <button onClick={() => setShowCreateSchedule(null)} className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-colors">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-white/60 mb-2">カレンダー名</label>
                    <input type="text" value={calendarName} onChange={(e) => setCalendarName(e.target.value)} className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 sm:py-3 text-sm bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-white/25" placeholder="例: 試験対策スケジュール" />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                    <button onClick={() => setShowCreateSchedule(null)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 text-white/50 bg-white/5 rounded-xl hover:bg-white/10 transition-colors font-semibold text-sm">キャンセル</button>
                    <button onClick={() => showCreateSchedule && createSchedule(showCreateSchedule)} className="w-full min-h-[44px] sm:min-h-[48px] px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-emerald-500/20 text-sm">作成する</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Modal */}
          {openCalendarGroupId && groupSchedules[openCalendarGroupId] && (
            <GroupScheduleCalendar
              groupId={openCalendarGroupId}
              scheduleId={groupSchedules[openCalendarGroupId].id}
              calendarName={groupSchedules[openCalendarGroupId].calendar_name}
              onClose={() => setOpenCalendarGroupId(null)}
              onUpdateCalendarName={(newName) => {
                setGroupSchedules(prev => ({
                  ...prev,
                  [openCalendarGroupId]: { ...prev[openCalendarGroupId], calendar_name: newName },
                }));
              }}
            />
          )}

          {/* Achievement Form Modal */}
          {showAchievementForm && selectedGroup && (
            <AchievementForm
              defaultGroupId={selectedGroup.id}
              onSuccess={() => {
                setShowAchievementForm(false);
                setAchievementRefreshKey(prev => prev + 1);
              }}
              onCancel={() => setShowAchievementForm(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
